import React from 'react';
import { useIntl } from 'react-intl';
import Tooltip from 'components/core/Tooltip';
import useCopyToClipboard from 'hooks/useClipboard';
import _get from 'lodash/get';
import { ReactComponent as CopyIcon } from 'assets/icons/copy.svg';
import { ReactComponent as CheckIcon } from 'assets/icons/check.svg';
import styles from './style.cssmodule.css';

// Components
import Status from '../Status/Status';
import ParkingType from '../ParkingType/ParkingType';
import Payment from '../Payment/Payment';
import WarningOccupancy from '../WarningOccupancy/WarningOccupancy';
import { createRedirectionLink } from '../../utils/utils';
import { ReactComponent as RedirectionIcon } from '../../icons/redirection.svg';

function ParkingCard(parking) {
  const { formatMessage: t } = useIntl();
  const {
    copyToClipboard, copied, justCopied, resetJustCopied,
  } = useCopyToClipboard();

  const firstname = _get(parking, 'customer.firstname', '');
  const lastname = _get(parking, 'customer.lastname', '');
  const email = _get(parking, 'customer.email', '-');
  const emailJustCopied = justCopied && copied && copied === email;
  const phone = _get(parking, 'customer.phone', '-');
  const phoneJustCopied = justCopied && copied && copied === phone;
  const parkingType = _get(parking, 'functionalType', null);
  const carparkId = _get(parking, 'carpark.id', null);
  const carparkName = _get(parking, 'carpark.name', '').replace(/^Parking\s/, '');
  const reference = _get(parking, 'reference', null);
  const vehicles = _get(parking, 'vehicles', []);
  const licensePlates = vehicles.map((v) => _get(v, 'licensePlate', null)).filter((lp) => lp);
  const firstLicensePlate = licensePlates.length > 0 ? licensePlates[0] : '';

  const licensePlate = vehicles.length > 0 ? _get(vehicles, '[0].licensePlate', null) : '';
  const status = _get(parking, 'status', null);

  // 👥 Username
  let Firstname = null;
  let Lastname = null;

  if (firstname) Firstname = <span className={styles.firstname}>{firstname}</span>;
  if (lastname) Lastname = <span className={styles.lastname}>{lastname}</span>;

  // ↪️ Redirection link
  const redirectionLink = createRedirectionLink({
    parkingType,
    carparkId,
    reference,
    licensePlate,
  });

  return (
    <div key={parking.id} className={styles.card}>
      {/* First row */}
      <div className={styles.firstRow}>
        <span className={styles.usernameOrLicensePlate}>
          {Firstname}
          {Firstname ? ' ' : null}
          {Lastname}
        </span>

        {/* External link */}
        <a href={redirectionLink} rel="noreferrer" target="_blank">
          <Tooltip content={t({ id: 'parking.table.actions.go' })} style={{ display: 'grid', placeItems: 'center' }}>
            <RedirectionIcon width="24" height="24" />
          </Tooltip>
        </a>
      </div>

      {/* Second row */}
      <div className={styles.secondRow}>
        <ParkingType type={parkingType} />
      </div>

      {/* Third row */}
      {email && (
        <div className={styles.thirdRow}>
          <span>{email}</span>
          <Tooltip content={t({ id: emailJustCopied ? 'tooltip.copied' : 'tooltip.copy' })}>
            <div
              className={styles.copyIcon}
              onMouseLeave={() => resetJustCopied({ delay: 100 })}
              onClick={() => copyToClipboard(email)}
            >
              {emailJustCopied ? <CheckIcon width="16" height="16" /> : <CopyIcon width="16" height="16" />}
            </div>
          </Tooltip>
        </div>
      )}

      {/* Fourth row */}
      {phone && (
        <div className={styles.fourthRow}>
          <span>{phone}</span>
          <Tooltip content={t({ id: phoneJustCopied ? 'tooltip.copied' : 'tooltip.copy' })}>
            <div
              className={styles.copyIcon}
              onMouseLeave={() => resetJustCopied({ delay: 100 })}
              onClick={() => copyToClipboard(phone)}
            >
              {phoneJustCopied ? <CheckIcon width="16" height="16" /> : <CopyIcon width="16" height="16" />}
            </div>
          </Tooltip>
        </div>
      )}

      {/* Fifth row */}
      <div className={styles.fifthRow}>
        <span className={styles.carpark}>{carparkName}</span>
      </div>

      {/* Sixth row */}
      <div className={styles.sixthRow}>
        <div className={styles.inline}>
          <span>
            <span>
              {' '}
              {t({ id: 'card.parking.reference' })}
              {' '}
            </span>
            <span>{reference}</span>
          </span>
          <span>|</span>
          <Payment parking={parking} />
        </div>
      </div>

      {/* Seventh row */}
      <div className={styles.seventhRow}>
        <div className={styles.inline}>
          <span className={styles.bold}>{licensePlates.length > 1 ? `${firstLicensePlate} (+${licensePlates.length - 1})` : firstLicensePlate}</span>
          <span>|</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Status status={status} />
            <WarningOccupancy parking={parking} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default ParkingCard;



// WEBPACK FOOTER //
// ./routes/cpm/routes/parkings/components/ParkingCard/ParkingCard.jsx